import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, Camera, AlertTriangle } from "lucide-react";

/**
 * Mobile-friendly live camera QR/barcode scanner (modal).
 *
 * Props:
 *   open       : boolean — whether the modal is shown
 *   onClose    : () => void
 *   onResult   : (decodedText: string) => void  — called once on first decode
 *   title      : optional heading
 *
 * Uses the rear ("environment") camera when available and falls back with a
 * clear message when camera permission is denied / unavailable.
 */
export default function QrScanner({ open, onClose, onResult, title = "Scan Coupon QR" }) {
  const regionId = useRef("qr-region-" + Math.random().toString(36).slice(2, 9));
  const scannerRef = useRef(null);
  const handledRef = useRef(false);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    if (!open) return;
    handledRef.current = false;
    setError("");
    setStarting(true);

    let cancelled = false;
    let running = false;
    let html5 = null;
    const timer = setTimeout(() => {
    if (cancelled || !document.getElementById(regionId.current)) return;
    html5 = new Html5Qrcode(regionId.current, { verbose: false });
    scannerRef.current = html5;

    // Larger, viewport-relative scan box + higher camera resolution so dense
    // signed/encrypted coupon QR codes (longer payloads than a plain URL QR)
    // resolve reliably instead of the camera just idling on "no code found".
    const config = {
      fps: 15,
      qrbox: (viewfinderWidth, viewfinderHeight) => {
        const size = Math.floor(Math.min(viewfinderWidth, viewfinderHeight) * 0.75);
        return { width: size, height: size };
      },
      aspectRatio: 1.0,
      experimentalFeatures: { useBarCodeDetectorIfSupported: true },
    };
    const videoConstraints = {
      facingMode: "environment",
      width: { ideal: 1920 },
      height: { ideal: 1080 },
    };

    const onSuccess = (decodedText) => {
      if (handledRef.current) return;
      handledRef.current = true;
      // stop then bubble up
      running = false;
      html5.stop().catch(() => {}).finally(() => {
        onResult && onResult(decodedText);
      });
    };

    // Pick the REAR camera explicitly by device label FIRST — many Android
    // browsers ignore the "environment" facingMode hint and default to the
    // front camera, so we enumerate devices and match by label before ever
    // trying the hint-based approach.
    Html5Qrcode.getCameras()
      .then((cams) => {
        if (!cams || !cams.length) throw new Error("no-cameras");
        const backCam =
          cams.find((c) => /back|rear|environment/i.test(c.label || "")) ||
          cams[cams.length - 1]; // on most phones the last listed camera is the rear one
        return html5.start({ deviceId: { exact: backCam.id } }, config, onSuccess, () => {});
      })
      .then(() => {
        running = true;
        if (cancelled) { running = false; html5.stop().catch(() => {}); }
        setStarting(false);
      })
      .catch(() => {
        // fallback: let the browser pick using the environment hint
        html5
          .start(videoConstraints, config, onSuccess, () => {})
          .then(() => { running = true; if (cancelled) { running = false; html5.stop().catch(() => {}); } setStarting(false); })
          .catch(() => {
            setStarting(false);
            setError(
              "Camera unavailable or permission denied. Please allow camera access, or paste/enter the coupon code manually."
            );
          });
      });

    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s && running) {
        try {
          s.stop().then(() => { try { s.clear(); } catch (e) { /* noop */ } }).catch(() => {});
        } catch (e) { /* noop */ }
      }
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" data-testid="qr-scanner-modal">
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 bg-slate-900 text-white">
          <Camera size={18} />
          <span className="font-semibold text-sm">{title}</span>
          <button className="ml-auto p-1 rounded hover:bg-white/10" onClick={onClose} data-testid="qr-scanner-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-4">
          {error && (
            <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3 mb-3" data-testid="qr-scanner-error">
              <AlertTriangle size={18} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div id={regionId.current} className={error ? "hidden" : "w-full rounded-lg overflow-hidden bg-black min-h-[240px]"} />
          {!error && (
            <p className="text-center text-xs text-slate-500 mt-3">
              {starting ? "Starting camera…" : "Point the camera at the coupon QR code"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
